import { useMemo, useState } from "react";
import { buildTranscriptTrimPreview } from "@/lib/lead/transcriptTrim";

type Modal = null | "edit" | "trim";

export function TranscriptEditTrimControls(props: {
  transcript: string;
  commitTranscript: (next: string) => void;
}) {
  const [modal, setModal] = useState<Modal>(null);
  const [editDraft, setEditDraft] = useState("");
  const trimPreview = useMemo(() => buildTranscriptTrimPreview(props.transcript), [props.transcript]);

  function openEdit() {
    setEditDraft(props.transcript);
    setModal("edit");
  }

  function saveEdit() {
    props.commitTranscript(editDraft);
    setModal(null);
  }

  function applyTrim() {
    props.commitTranscript(trimPreview.trimmed);
    setModal(null);
  }

  // When the lead has no transcript yet the action becomes "Upload transcript"
  // and renders as a substantial primary button so a manager can start a lead
  // by typing or pasting one in. With a transcript present, the action is
  // the lower-key "Edit transcript" link alongside Trim. See dev-sync note
  // 077dd4c7 — phase 1 (quick win); the Google Meet integration is tracked
  // in docs/planning/lead_meeting_links.md.
  const isUploadMode = !props.transcript;

  return (
    <>
      <div className="flex flex-wrap gap-2 justify-end items-center">
        {isUploadMode ? (
          <button
            type="button"
            onClick={openEdit}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:bg-indigo-500 dark:hover:bg-indigo-400"
          >
            Upload transcript
          </button>
        ) : (
          <button
            type="button"
            onClick={openEdit}
            className="text-[11px] font-medium text-indigo-700 hover:underline dark:text-indigo-400"
          >
            Edit transcript
          </button>
        )}
        <button
          type="button"
          onClick={() => setModal("trim")}
          disabled={!props.transcript.trim()}
          className="text-[11px] font-medium text-indigo-700 hover:underline disabled:opacity-40 dark:text-indigo-400"
        >
          Trim transcript
        </button>
      </div>

      {modal === "edit" ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="transcript-edit-title"
        >
          <div className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-600 dark:bg-zinc-900">
            <h2 id="transcript-edit-title" className="px-4 pt-4 text-base font-semibold text-gray-900 dark:text-gray-100">
              {isUploadMode ? "Upload transcript" : "Edit transcript"}
            </h2>
            <p className="px-4 text-xs text-gray-500 dark:text-gray-400">
              {isUploadMode
                ? "Paste or type a transcript to start this lead. Changes are saved to this lead only."
                : "Changes are saved to this lead only."}
            </p>
            <textarea
              className="mx-4 my-3 min-h-[200px] flex-1 rounded-xl border border-gray-200 p-3 text-sm font-mono text-gray-900 dark:border-gray-600 dark:bg-zinc-950 dark:text-gray-100"
              placeholder={isUploadMode ? "Paste the call transcript here…" : undefined}
              value={editDraft}
              onChange={(e) => setEditDraft(e.target.value)}
            />
            <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 px-4 py-3 dark:border-gray-700">
              <button
                type="button"
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 dark:border-gray-600 dark:text-gray-100"
                onClick={() => setModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                onClick={saveEdit}
              >
                Save transcript
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {modal === "trim" ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="transcript-trim-title"
        >
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-600 dark:bg-zinc-900">
            <h2 id="transcript-trim-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Trim transcript
            </h2>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Removes long stretches of low-information chatter where safe, keeps product, pricing, site or delivery,
              contact and location, and related deal detail. Review the preview — heuristics can be wrong on edge cases.
            </p>
            <ul className="mt-3 list-disc pl-5 text-xs text-gray-700 dark:text-gray-300 space-y-0.5">
              {trimPreview.summaryLines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
            {trimPreview.removedFluffSections.length > 0 ? (
              <div className="mt-3 rounded-lg border border-amber-200/90 bg-amber-50/60 px-3 py-2 dark:border-amber-800/60 dark:bg-amber-950/25">
                <div className="text-[11px] font-medium text-amber-950 dark:text-amber-100">
                  Sections targeted for removal (preview)
                </div>
                <ul className="mt-1.5 space-y-1 text-[10px] text-amber-950/95 dark:text-amber-100/90 font-mono whitespace-pre-wrap">
                  {trimPreview.removedFluffSections.slice(0, 6).map((s, i) => (
                    <li key={i} className="border-t border-amber-200/50 pt-1 first:border-t-0 first:pt-0 dark:border-amber-800/40">
                      {s.preview}
                    </li>
                  ))}
                </ul>
                {trimPreview.removedFluffSections.length > 6 ? (
                  <p className="mt-1 text-[10px] text-amber-900/80 dark:text-amber-200/80">
                    + {trimPreview.removedFluffSections.length - 6} more section
                    {trimPreview.removedFluffSections.length - 6 === 1 ? "" : "s"}
                  </p>
                ) : null}
              </div>
            ) : null}
            <div className="mt-3 grid gap-2 sm:grid-cols-2 text-xs">
              <div>
                <div className="font-medium text-gray-800 dark:text-gray-200 mb-1">Current transcript</div>
                <pre className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-2 font-mono text-gray-800 whitespace-pre-wrap dark:border-gray-600 dark:bg-zinc-950 dark:text-gray-200">
                  {props.transcript || "—"}
                </pre>
              </div>
              <div>
                <div className="font-medium text-gray-800 dark:text-gray-200 mb-1">After trim (preview)</div>
                <pre className="max-h-40 overflow-y-auto rounded-lg border border-emerald-200/90 bg-emerald-50/50 p-2 font-mono text-gray-800 whitespace-pre-wrap dark:border-emerald-800/50 dark:bg-emerald-950/25 dark:text-gray-200">
                  {trimPreview.trimmed || "—"}
                </pre>
              </div>
            </div>
            <p className="mt-2 text-[10px] text-gray-500 dark:text-gray-500">
              Review the summary, removed-section previews, and both columns before applying.
            </p>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 dark:border-gray-600 dark:text-gray-100"
                onClick={() => setModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={props.transcript === trimPreview.trimmed}
                className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                onClick={applyTrim}
              >
                Apply trim
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
